import type { NewGoal } from "../../shared/goals/api";
import type { GoalConfig } from "../../shared/goals/config";
import type { Plan } from "../../shared/goals/plan";
import type { RunState } from "../../shared/goals/runs";
import type { PixelStep } from "./audienceApi";
import { post, request } from "./api";

export type GoalState = "setting_up" | "planning" | "plan_ready" | "getting_ready" | "live" | "paused" | "done";

export interface PlanComment {
  id: string;
  version: number;
  section: string;
  body: string;
  sent_at: string | null;
  created_at: string;
}

/** A goal as GET /goals/:id returns it: the goal, the version in force, the plan waiting for review, and its run. */
export interface GoalView {
  goal: {
    id: string;
    productId: string;
    sentence: string;
    state: GoalState;
    version: number;
    setupRev: number;
    setup: GoalConfig | null;
    launchedAt: string | null;
    /** The goal's Meta campaign: creating, unsure, paused or on; null before Launch. */
    campaign: string | null;
    endedAt: string | null;
  };
  current: { version: number; config: GoalConfig; plan: Plan | null; summary: string } | null;
  proposal: { id: string; version: number; summary: string; plan: Plan | null; madeFromCurrentSetup: boolean; createdAt: string } | null;
  planRun: { id: string; state: RunState; error: string | null; created_at: string; ended_at: string | null } | null;
  comments: PlanComment[];
}

const goal = (id: string) => `/goals/${encodeURIComponent(id)}`;

export const goalsApi = {
  create: (productId: string, body: NewGoal) => post<{ id: string }>(`/products/${encodeURIComponent(productId)}/goals`, body),
  /** Look for the product's Meta pixel in a page, before the goal exists. */
  pixelCheck: (productId: string, page: string) => post<{ pixel: PixelStep }>(`/products/${encodeURIComponent(productId)}/pixel-check`, { page }),
  get: (id: string) => request<GoalView>(goal(id)),
  /** A JSON merge patch of the goal's setup. Arrays are replaced whole. */
  patchSetup: (id: string, patch: Record<string, unknown>) =>
    request<{ setupRev: number; config: GoalConfig }>(`${goal(id)}/setup`, { method: "PATCH", body: JSON.stringify({ patch }) }),
  /** Ask for a plan, or a new one: unsent comments go to the agent with it. */
  plan: (id: string) => post<{ runId: string }>(`${goal(id)}/plan`),
  /** The agent drafts the product's playbook for the Playbook step. */
  playbook: (id: string) => post<{ runId: string }>(`${goal(id)}/playbook`),
  comment: (id: string, version: number, body: { section: string; body: string }) => post<{ id: string }>(`${goal(id)}/versions/${version}/comments`, body),
  /** Approve a plan; the chat gets a line about it. */
  approve: (id: string, versionId: string) => post<{ version: number }>(`${goal(id)}/approve`, { versionId }),
};
